import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { products, categories } from '@/mocks/products';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import QuotePopup from '@/pages/home/components/QuotePopup';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const category = categories.find((c) => c.slug === slug);
  const categoryProducts = products.filter((p) => p.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-black text-gray-900 mb-2">Category Not Found</h1>
          <Link to="/" className="text-yellow-600 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* Hero */}
      <section className="relative h-[320px] md:h-[400px] overflow-hidden">
        <img src={category.image} alt={category.name} className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex items-center h-full">
          <div className="max-w-[1420px] mx-auto px-6 w-full pt-16">
            <nav className="flex items-center gap-2 text-xs text-white/70 mb-4">
              <Link to="/" className="hover:text-yellow-400">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-white">{category.name}</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1.5 rounded-full mb-3">
              <i className={category.icon} />
              {categoryProducts.length} Products
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-3">{category.name}</h1>
            <p className="text-white/80 text-sm max-w-xl">{category.description}</p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-14">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-black text-gray-900">All {category.name} Products</h2>
            <button
              onClick={() => setQuoteOpen(true)}
              className="px-5 py-2 bg-yellow-400 text-gray-900 text-sm font-bold rounded-lg hover:bg-yellow-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get a Quote
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {categoryProducts.map((product) => (
              <Link
                key={product.slug}
                to={`/products/item/${product.slug}`}
                className="group block bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-yellow-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="w-full h-[200px] overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-black text-gray-900 group-hover:text-yellow-700 transition-colors mb-1">{product.name}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{product.tagline}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {product.standards.slice(0, 2).map((s) => (
                        <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{s}</span>
                      ))}
                    </div>
                    <span className="text-yellow-600 text-xs font-bold flex items-center gap-1 whitespace-nowrap">
                      Details <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Other Categories */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-[1420px] mx-auto px-6">
          <h2 className="text-xl font-black text-gray-900 mb-6">Browse Other Categories</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {categories.filter((c) => c.slug !== slug).map((cat) => (
              <Link
                key={cat.slug}
                to={`/products/${cat.slug}`}
                className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-yellow-300 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 rounded-lg flex-shrink-0">
                  <i className={`${cat.icon} text-gray-900 text-xl`} />
                </div>
                <div>
                  <div className="text-sm font-black text-gray-900 group-hover:text-yellow-700 transition-colors">{cat.name}</div>
                  <div className="text-xs text-gray-500">{products.filter((p) => p.categorySlug === cat.slug).length} products</div>
                </div>
                <i className="ri-arrow-right-line text-gray-400 ml-auto group-hover:text-yellow-500 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-gray-900">
        <div className="max-w-[1420px] mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-3">Need a Custom Quote?</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-xl mx-auto">
            Tell us your specifications and we'll source the best price from our mill partners within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setQuoteOpen(true)}
              className="px-8 py-3 bg-yellow-400 text-gray-900 font-black rounded-lg hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Request a Quote
            </button>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 border-2 border-white text-white font-black rounded-lg hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <FooterSection />
      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}
